import type { Accent, WordTiming } from '../types';
import { loadKeys, loadProviders, effectiveStt } from './keys';
import { appPasswordHeader } from './server';

// Client → local proxy Worker. Keys ride in a request header (never the URL,
// never persisted server-side). The Worker forwards to the provider and returns
// audio bytes / transcript JSON.

// Accent → provider voice mapping. Cloud voices are approximate for OpenAI
// (its voices are largely neutral-American); ElevenLabs voices are genuinely
// accent-distinct. The browser fallback picks a true OS voice by BCP-47 lang.
export const VOICE_MAP: Record<'openai' | 'elevenlabs', Record<Accent, string>> = {
  openai: { 'en-GB': 'fable', 'en-US': 'alloy', 'en-AU': 'ash' },
  elevenlabs: {
    'en-GB': 'JBFqnCBsd6RMkjVDRZzb', // George — British
    'en-US': '21m00Tcm4TlvDq8ikWAM', // Rachel — American
    'en-AU': 'IKne3meq5aSn9XLyUdCD', // Charlie — Australian
  },
};

export const ACCENT_LABEL: Record<Accent, string> = {
  'en-GB': 'British',
  'en-US': 'American',
  'en-AU': 'Australian',
};

export class ProviderError extends Error {
  constructor(message: string, readonly kind: 'no_key' | 'provider' | 'network') {
    super(message);
  }
}

/** Synthesize a question to an audio Blob via the proxy. Throws ProviderError. */
export async function synthesize(
  text: string,
  accent: Accent,
  provider: 'workers' | 'openai' | 'elevenlabs',
): Promise<{ blob: Blob; mime: string }> {
  const cfg = loadProviders();
  let headers: Record<string, string> = { 'content-type': 'application/json' };
  let payload: Record<string, unknown> = { provider, text };

  if (provider === 'workers') {
    // Server voice — no key, just the access-password header (if gated).
    headers = { ...headers, ...appPasswordHeader() };
  } else {
    const keys = loadKeys();
    const apiKey = provider === 'openai' ? keys.openai : keys.elevenlabs;
    if (!apiKey) throw new ProviderError(`No ${provider} key`, 'no_key');
    headers['x-provider-key'] = apiKey;
    if (provider === 'openai') headers['x-openai-base'] = cfg.openaiBaseUrl;
    payload = { provider, text, voice: VOICE_MAP[provider][accent], accent, model: cfg.ttsModel };
  }

  let res: Response;
  try {
    res = await fetch('/api/tts', { method: 'POST', headers, body: JSON.stringify(payload) });
  } catch {
    throw new ProviderError('Could not reach the voice service', 'network');
  }
  if (!res.ok) {
    const msg = await safeErr(res);
    throw new ProviderError(msg, res.status === 401 ? 'no_key' : 'provider');
  }
  const mime = res.headers.get('content-type') || 'audio/mpeg';
  const blob = await res.blob();
  return { blob, mime };
}

/** Transcribe an answer recording. Returns text + word timings. Throws ProviderError. */
export async function transcribe(audio: Blob): Promise<{ text: string; words: WordTiming[] }> {
  const stt = effectiveStt();
  if (!stt) throw new ProviderError('No transcription available', 'no_key');
  const cfg = loadProviders();

  const fd = new FormData();
  fd.append('audio', audio, 'answer.webm');
  fd.append('provider', stt);

  let headers: Record<string, string> = {};
  if (stt === 'workers') {
    headers = appPasswordHeader();
  } else {
    const keys = loadKeys();
    if (!keys.openai) throw new ProviderError('No OpenAI key for transcription', 'no_key');
    headers = { 'x-provider-key': keys.openai, 'x-openai-base': cfg.openaiBaseUrl };
    fd.append('model', cfg.sttModel);
  }

  let res: Response;
  try {
    res = await fetch('/api/stt', { method: 'POST', headers, body: fd });
  } catch {
    throw new ProviderError('Could not reach the transcription service', 'network');
  }
  if (!res.ok) {
    const msg = await safeErr(res);
    throw new ProviderError(msg, res.status === 401 ? 'no_key' : 'provider');
  }
  const data = (await res.json()) as { text: string; words: WordTiming[] };
  return { text: (data.text || '').trim(), words: data.words || [] };
}

async function safeErr(res: Response): Promise<string> {
  try {
    const j = await res.json();
    return (j && (j.error || j.message)) || `Provider error ${res.status}`;
  } catch {
    return `Provider error ${res.status}`;
  }
}
